// 마을 이동 연출(D3a, PRD 2.2): n-10 첫 클리어 뒤 귀환·워프 때 짧은 페이드(어둡게 → 밝게)와 화면 위쪽 가장자리의 지역 제목 카드.
// 연출 동안(game flow.travelTo > 0) 영웅 입력은 게임이 막는다. 카드는 위쪽 가장자리 띠에만 두고 가운데 플레이 영역을 가리지 않는다(입력도 받지 않는다).
// DOM은 값이 바뀔 때만 쓴다. 섬광 없음: 페이드는 검정 반투명이 천천히 오르내리기만 한다.
import { REGIONS_90D, TOWNS_90D } from '../shared/content/world';
import { townTagline } from '../shared/townStory';
import { Scene, type GameView } from '../shared/types';
import { C, FONT, TEXT_OUTLINE, el } from './dom';
import { viewW } from '../shared/viewport';

/** 이동 방식 문구(world.ts RegionDef.travel) */
const TRAVEL_TEXT: Readonly<Record<string, string>> = {
  'gate-walk': '돌문을 지나 걸어서', ship: '잿불 강을 배로 건너', 'cutin-walk': '서리 고개를 넘어', cutin: '거대 거북의 등을 타고', ending: '',
};

/** 진행률 t(0..1) → 페이드 불투명도: 앞 20%에서 0.85까지 어두워지고(마을이 바뀌는 순간을 가린다), 나머지 동안 천천히 걷힌다 */
export function travelFade(t: number): number {
  if (!(t > 0) || t >= 1) return 0;
  return t < 0.2 ? 0.85 * (t / 0.2) : 0.85 * (1 - (t - 0.2) / 0.8);
}

/**
 * 카드 너비(CSS px): 게임 화면 너비(viewport.viewW — 세로 폰을 돌린 화면에서도 게임 기준) 60%(최대 360) 이상, 72% 이하.
 * vw는 돌린 화면에서 실제 세로 폭이 되어 틀리므로 쓰지 않는다(PR #160 리뷰 P2)
 */
export function travelCardWidths(w: number): { min: number; max: number } {
  return { min: Math.round(Math.min(360, w * 0.6)), max: Math.round(w * 0.72) };
}

/** 카드 문구: 도착 마을 번호·이름, 그 마을에서 여는 지역, 이동 방식, 그 마을의 사정 한 줄(TOWN-SCENARIO) */
export function travelText(to: number, from: number): { title: string; sub: string; story: string } {
  const town = TOWNS_90D[to - 1];
  const name = town ? town.name : '';
  // 마을 n+1로 오는 길 = 지역 n의 이동 연출(바로 다음 마을이 아니면 워프 문구)
  const back = to !== from + 1;
  const via = !back && to >= 2 ? TRAVEL_TEXT[REGIONS_90D[to - 2].travel] ?? '' : '';
  const next = REGIONS_90D[to - 1];
  return {
    title: `마을 ${'①②③④⑤⑥'[to - 1] ?? to} ${name}`,
    sub: back ? '워프 게이트로 이동했다' : `${via ? via + ' 도착 · ' : ''}${next ? next.name + '으로 가는 길' : ''}`,
    story: townTagline(to),
  };
}

export class TravelCard {
  private readonly fade: HTMLDivElement;
  private readonly card: HTMLDivElement;
  private readonly title: HTMLDivElement;
  private readonly sub: HTMLDivElement;
  private readonly story: HTMLDivElement;
  private shown = false;
  private key = '';
  private kOpacity = -1;
  private kW = -1;

  constructor(parent: HTMLElement, private readonly view: GameView) {
    this.fade = el('div', 'position:absolute;inset:0;background:#05070d;opacity:0;pointer-events:none;display:none;z-index:30;', parent);
    this.fade.dataset.testid = 'travel-fade';
    this.card = el('div', 'position:absolute;left:50%;top:max(8px,env(safe-area-inset-top,0px));transform:translateX(-50%);display:none;pointer-events:none;z-index:31;' +
      'padding:6px 18px 7px;box-sizing:border-box;text-align:center;border-radius:0 0 14px 14px;' +
      'border:2px solid ' + C.gold + ';border-top:none;background:linear-gradient(180deg,rgba(16,22,42,.94),rgba(26,34,56,.9));box-shadow:0 4px 14px rgba(0,0,0,.5);', parent);
    this.card.dataset.testid = 'travel-card';
    this.card.setAttribute('role', 'status');
    this.title = el('div', 'font:900 20px ' + FONT + ';color:#fff;letter-spacing:1px;' + TEXT_OUTLINE, this.card, '');
    this.sub = el('div', 'margin-top:2px;font:700 13px ' + FONT + ';color:' + C.gold + ';word-break:keep-all;', this.card, '');
    this.story = el('div', 'margin-top:3px;font:italic 700 12px ' + FONT + ';color:#d8e2f2;word-break:keep-all;', this.card, '');
    this.story.dataset.testid = 'travel-story';
  }

  get visible(): boolean { return this.shown; }

  update(): void {
    const f = this.view.flow;
    const on = f.scene === Scene.Village && f.travelTo > 0 && f.travelTotal > 0;
    if (!on) {
      if (this.shown) { this.shown = false; this.fade.style.display = 'none'; this.card.style.display = 'none'; this.key = ''; this.kOpacity = -1; }
      return;
    }
    if (!this.shown) { this.shown = true; this.fade.style.display = 'block'; this.card.style.display = 'block'; }
    const w = viewW();
    if (w !== this.kW) { this.kW = w; const cw = travelCardWidths(w); this.card.style.minWidth = cw.min + 'px'; this.card.style.maxWidth = cw.max + 'px'; }
    const key = f.travelTo + ':' + f.travelFrom;
    if (key !== this.key) {
      this.key = key;
      const t = travelText(f.travelTo, f.travelFrom);
      this.title.textContent = t.title;
      this.sub.textContent = t.sub;
      this.story.textContent = t.story ? '“' + t.story + '”' : '';
      this.story.style.display = t.story ? 'block' : 'none';
    }
    const o = Math.round(travelFade(f.travelTick / f.travelTotal) * 100) / 100;
    if (o !== this.kOpacity) { this.kOpacity = o; this.fade.style.opacity = String(o); }
  }
}
